import { useReducer } from 'react';

import invert from 'lodash/invert';

import { useHotelOffersApiMetrics } from '../hooks/useHotelOffersApiMetrics';

import useHotelOffersFetch from './useHotelOffersFetch';

import type { Context, HotelOffersState } from '../types/hotel-offers';
import type {
  Filter,
  PriceRange,
  PriceType,
} from '@shared/types/frontend/ubff/hotel-detail-price';
import type { UBFFGateway } from '@shared/types/frontend/ubff-gateway';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { Nullable, Stay } from '@shared/types/utils';

import { PRICE_TYPE_MAPPING } from '@shared/constants/ubff-gateway';
import {
  buildExperiments,
  buildHopsVersion,
  buildTravellerContext,
} from '@shared/data-access-frontend/ubff-gateway/build-params';
import useUpdatedActionAfterLogingIn from '@shared/utils-frontend/hooks/useActionAfterLoggingIn';
import { DATE_FORMAT } from '@shared/utils-universal/i18n/date-formats';

const PRICE_TYPE_MAPPING_TO_ORIGIN = invert(PRICE_TYPE_MAPPING);

type Props = {
  hotelId: string;
  entityId?: string;
  searchId?: string;
  dayViewCorrelationId?: string;
  stay: Stay;
  uBFFGateway: UBFFGateway;
  isSeoTraffic: boolean;
  initialPriceType: Nullable<string>;
  initialFilters?: Filter[];
  priceRanges?: PriceRange[];
  i18n: I18nService;
  userContext: UserContext;
};

const reducer = (
  state: HotelOffersState,
  update: Partial<HotelOffersState>,
) => ({ ...state, ...update });

const useHotelOffersStore = ({
  dayViewCorrelationId,
  entityId,
  hotelId,
  i18n,
  initialFilters,
  initialPriceType,
  isSeoTraffic,
  priceRanges,
  searchId,
  stay,
  uBFFGateway,
  userContext,
}: Props): Context => {
  const { bellboyTVersion, showDebugInfo } = userContext;

  const [state, setState] = useReducer(reducer, {
    request: {
      hotelId,
      entityId,
      searchId,
      filters: initialFilters,
      priceType: PRICE_TYPE_MAPPING_TO_ORIGIN[
        initialPriceType ?? 'price-per-night'
      ] as PriceType,
      travellerContext: buildTravellerContext(i18n, stay),
      tVersion: bellboyTVersion ?? undefined,
      experiments: buildExperiments(userContext),
      sortedPartner: buildHopsVersion(userContext),
      partnerSortingContext: {
        sortedPartner: buildHopsVersion(userContext),
        defaultDate: false,
        searchTimeOfDay: i18n.formatDate(
          new Date(),
          DATE_FORMAT.NON_LOCALISED_FULL,
        ),
      },
      requestContext: {
        debug: showDebugInfo,
      },
      requestFilters:
        priceRanges && priceRanges.length > 0
          ? {
              priceRangeFilter: priceRanges,
            }
          : undefined,
    },
    priceContext: {
      hotelId,
      dayViewCorrelationId,
      searchId,
      isSeoTraffic,
      stay,
    },
    response: null,
    error: null,
  });

  const { fetchResults } = useHotelOffersFetch({
    request: state.request,
    setState,
    uBFFGateway,
  });

  useHotelOffersApiMetrics({
    request: state.request,
    response: state.response,
    error: state.error,
  });

  useUpdatedActionAfterLogingIn({
    callback: fetchResults,
  });

  return {
    state,

    actions: {
      applyFilters: (filters: Filter[]) => {
        setState({ response: null, request: { ...state.request, filters } });
      },
      applyFiltersWithPriceRanges: (
        filters: Filter[],
        updatedPriceRanges: PriceRange[],
      ) => {
        setState({
          response: null,
          request: {
            ...state.request,
            filters,
            requestFilters:
              updatedPriceRanges.length > 0
                ? { priceRangeFilter: updatedPriceRanges }
                : undefined,
          },
        });
      },
      applyStay: (newStay: Stay) => {
        setState({
          response: null,
          request: {
            ...state.request,
            travellerContext: buildTravellerContext(i18n, newStay),
          },
          priceContext: {
            ...state.priceContext,
            stay: newStay,
          },
        });
      },
      applyPriceType: (priceType: PriceType) => {
        setState({ response: null, request: { ...state.request, priceType } });
      },
    },
  };
};

export default useHotelOffersStore;
